import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { ZoningZoneReference } from '@popety-io/types'
import { Modal } from '../../../../molecules/Modal'
import { Icon } from '../../../../atoms/Icon'
import { Text } from '../../../../atoms/Text'
import { PDFViewer } from '../../../../organisms/PDFViewer'
import { SearchBar } from '../DocumentsSection/DocumentBrowser/SearchBar'
import { DocumentSection } from '../DocumentsSection/DocumentBrowser/DocumentSection'
import {
  ModalContainer,
  SplitContainer,
  DocumentBrowserPanel,
  PDFViewerPanel as PDFPanel,
  ModalHeader,
  ModalHeaderTopRow,
  ModalHeaderContext,
  ModalHeaderChevron,
  ModalHeaderDivider,
  ModalTitle,
  CloseButton,
} from '../DocumentsSection/DocumentViewerModal/DocumentViewerModal.styles'

/** A single page reference within a document */
interface PageReference {
  page: number
  pageEnd?: number
  indices: string[]
}

/** References grouped by document, with per-page metric indices */
interface GroupedDocument {
  documentId: number
  documentTitle: string
  documentUrl?: string
  pageRefs: PageReference[]
  totalRefs: number
}

export interface ReferenceViewerModalTranslations {
  title?: string
  searchPlaceholder?: string
  searchAriaLabel?: string
  clearSearchAriaLabel?: string
  noReferenceSelected?: string
  selectReferenceHint?: string
  noPreviewAvailable?: string
  noPreviewHint?: string
  noReferences?: string
  /** Single page label; use `{{page}}` interpolation, e.g. "Page {{page}}" */
  pageLabel?: string
  /** Page range label; `{{start}}` and `{{end}}`, e.g. "Pages {{start}}–{{end}}" */
  pagesLabel?: string
}

const DEFAULT_TRANSLATIONS: ReferenceViewerModalTranslations = {
  title: 'Regulation References',
  searchPlaceholder: 'Search references...',
  searchAriaLabel: 'Search references',
  clearSearchAriaLabel: 'Clear search',
  noReferenceSelected: 'No Reference Selected',
  selectReferenceHint:
    'Select a reference from the list to view the regulation document',
  noPreviewAvailable: 'No Preview Available',
  noPreviewHint: 'This document does not have a PDF preview URL available',
  noReferences: 'No references available',
  pageLabel: 'Page {{page}}',
  pagesLabel: 'Pages {{start}}–{{end}}',
}

const formatPage = (template: string, page: number) =>
  template.replace('{{page}}', String(page))

const formatPages = (template: string, start: number, end: number) =>
  template.replace('{{start}}', String(start)).replace('{{end}}', String(end))

interface ReferenceViewerModalProps {
  isOpen: boolean
  onClose: () => void
  references: ZoningZoneReference[]
  /** Pre-select this document + page when opening */
  initialDocumentId?: number
  initialPage?: number
  translations?: ReferenceViewerModalTranslations
  metricLabelMap?: Record<string, string>
  /** Zone context for breadcrumb header */
  municipalityName?: string
  planName?: string
  zoneName?: string
  /** Optional: transform a PDF URL through a proxy to bypass CORS */
  getPdfProxyUrl?: (url: string) => string
}

/**
 * Group references by document_id, then by page within each document.
 * Each document becomes an accordion section, each page a list item.
 */
const groupByDocument = (refs: ZoningZoneReference[]): GroupedDocument[] => {
  const map = new Map<
    number,
    {
      documentId: number
      documentTitle: string
      documentUrl?: string
      pageMap: Map<number, { pageEnd?: number; indices: string[] }>
    }
  >()

  for (const ref of refs) {
    if (!ref.document_id || !ref.document_title) continue

    let doc = map.get(ref.document_id)
    if (!doc) {
      doc = {
        documentId: ref.document_id,
        documentTitle: ref.document_title,
        documentUrl: ref.document_url,
        pageMap: new Map(),
      }
      map.set(ref.document_id, doc)
    }

    const page = ref.page_start || 1
    const existing = doc.pageMap.get(page) || { indices: [] }
    // Track the highest page_end for this page_start
    if (ref.page_end && ref.page_end > page) {
      existing.pageEnd = Math.max(existing.pageEnd || page, ref.page_end)
    }
    if (ref.indices) {
      for (const idx of ref.indices) {
        if (!existing.indices.includes(idx)) {
          existing.indices.push(idx)
        }
      }
    }
    doc.pageMap.set(page, existing)
  }

  return Array.from(map.values()).map((doc) => {
    const pageRefs = Array.from(doc.pageMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([page, data]) => ({
        page,
        pageEnd: data.pageEnd,
        indices: data.indices,
      }))

    return {
      documentId: doc.documentId,
      documentTitle: doc.documentTitle,
      documentUrl: doc.documentUrl,
      pageRefs,
      totalRefs: pageRefs.length,
    }
  })
}

const formatMetricLabel = (
  key: string,
  labelMap?: Record<string, string>
): string => {
  if (labelMap?.[key]) return labelMap[key]
  const base = key.replace(/_(min|max|value)$/, '')
  if (labelMap?.[base]) return labelMap[base]
  return base.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const ReferenceViewerModal: React.FC<ReferenceViewerModalProps> = ({
  isOpen,
  onClose,
  references,
  initialDocumentId,
  initialPage,
  translations,
  metricLabelMap,
  municipalityName,
  planName,
  zoneName,
  getPdfProxyUrl,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const grouped = useMemo(() => groupByDocument(references), [references])

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDocId, setSelectedDocId] = useState<number | null>(
    initialDocumentId || grouped[0]?.documentId || null
  )
  const [selectedPage, setSelectedPage] = useState<number>(
    initialPage || grouped[0]?.pageRefs[0]?.page || 1
  )
  const [selectedPageEnd, setSelectedPageEnd] = useState<number | undefined>(
    grouped[0]?.pageRefs[0]?.pageEnd
  )

  const selectedDoc =
    grouped.find((g) => g.documentId === selectedDocId) || null

  // Filter documents based on search query
  const filteredGrouped = useMemo(() => {
    if (!searchQuery.trim()) return grouped
    const query = searchQuery.toLowerCase()
    return grouped.filter(
      (doc) =>
        doc.documentTitle.toLowerCase().includes(query) ||
        doc.pageRefs.some((pr) =>
          pr.indices.some((idx) =>
            formatMetricLabel(idx, metricLabelMap).toLowerCase().includes(query)
          )
        )
    )
  }, [grouped, searchQuery, metricLabelMap])

  const handleSelectPage = (docId: number, page: number, pageEnd?: number) => {
    setSelectedDocId(docId)
    setSelectedPage(page)
    setSelectedPageEnd(pageEnd)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={true}
      showCloseButton={false}
    >
      <ModalContainer>
        <ModalHeader>
          {(municipalityName || planName || zoneName) && (
            <>
              <ModalHeaderContext>
                {municipalityName && <span>{municipalityName}</span>}
                {municipalityName && (planName || zoneName) && (
                  <ModalHeaderChevron />
                )}
                {planName && <span>{planName}</span>}
                {planName && zoneName && <ModalHeaderChevron />}
                {zoneName && <span>{zoneName}</span>}
              </ModalHeaderContext>
              <ModalHeaderDivider />
            </>
          )}
          <ModalHeaderTopRow>
            <ModalTitle>{t.title}</ModalTitle>
            <CloseButton onClick={onClose} type="button" aria-label="Close">
              <Icon name="x" size="medium" />
            </CloseButton>
          </ModalHeaderTopRow>
        </ModalHeader>

        <SplitContainer>
          {/* Left panel — reference browser (matches DocumentBrowser) */}
          <DocumentBrowserPanel>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              translations={{
                placeholder: t.searchPlaceholder,
                searchAriaLabel: t.searchAriaLabel,
                clearAriaLabel: t.clearSearchAriaLabel,
              }}
            />

            <ReferenceList>
              {filteredGrouped.map((doc, index) => (
                <DocumentSection
                  key={doc.documentId}
                  title={doc.documentTitle}
                  icon="file-text"
                  count={doc.totalRefs}
                  isLoading={false}
                  defaultExpanded={
                    doc.documentId ===
                      (initialDocumentId || grouped[0]?.documentId) ||
                    index === 0
                  }
                  translations={{ noDocuments: t.noReferences }}
                >
                  {doc.pageRefs.map((pr) => {
                    const isSelected =
                      selectedDocId === doc.documentId &&
                      selectedPage === pr.page
                    return (
                      <ReferenceListItem
                        key={pr.page}
                        onClick={() =>
                          handleSelectPage(doc.documentId, pr.page, pr.pageEnd)
                        }
                        $isSelected={isSelected}
                        type="button"
                      >
                        <RefIconWrapper>
                          <Icon name="bookmark" size="small" />
                        </RefIconWrapper>
                        <RefContent>
                          <RefTitle>
                            {pr.pageEnd && pr.pageEnd > pr.page
                              ? formatPages(
                                  t.pagesLabel || 'Pages {{start}}–{{end}}',
                                  pr.page,
                                  pr.pageEnd
                                )
                              : formatPage(
                                  t.pageLabel || 'Page {{page}}',
                                  pr.page
                                )}
                          </RefTitle>
                          {pr.indices.length > 0 && (
                            <RefMeta>
                              {pr.indices
                                .map((idx) =>
                                  formatMetricLabel(idx, metricLabelMap)
                                )
                                .join(' · ')}
                            </RefMeta>
                          )}
                        </RefContent>
                      </ReferenceListItem>
                    )
                  })}
                </DocumentSection>
              ))}
            </ReferenceList>
          </DocumentBrowserPanel>

          {/* Right panel — PDF viewer (matches PDFViewerPanel) */}
          <PDFPanel>
            {!selectedDoc ? (
              <EmptyStateContainer>
                <EmptyStateIcon>
                  <Icon name="file-text" size="large" />
                </EmptyStateIcon>
                <EmptyStateTitle>{t.noReferenceSelected}</EmptyStateTitle>
                <EmptyStateDescription>
                  {t.selectReferenceHint}
                </EmptyStateDescription>
              </EmptyStateContainer>
            ) : !selectedDoc.documentUrl ? (
              <PanelContainer>
                <DocumentHeader>
                  <DocumentTitle>{selectedDoc.documentTitle}</DocumentTitle>
                  <DocumentMeta>
                    {selectedPageEnd && selectedPageEnd > selectedPage
                      ? formatPages(
                          t.pagesLabel || 'Pages {{start}}–{{end}}',
                          selectedPage,
                          selectedPageEnd
                        )
                      : formatPage(
                          t.pageLabel || 'Page {{page}}',
                          selectedPage
                        )}
                  </DocumentMeta>
                </DocumentHeader>
                <EmptyStateContainer>
                  <EmptyStateIcon>
                    <Icon name="alert-circle" size="large" />
                  </EmptyStateIcon>
                  <EmptyStateTitle>{t.noPreviewAvailable}</EmptyStateTitle>
                  <EmptyStateDescription>
                    {t.noPreviewHint}
                  </EmptyStateDescription>
                </EmptyStateContainer>
              </PanelContainer>
            ) : (
              <PanelContainer>
                <DocumentHeader>
                  <DocumentTitle>{selectedDoc.documentTitle}</DocumentTitle>
                  <DocumentMeta>
                    {selectedPageEnd && selectedPageEnd > selectedPage
                      ? formatPages(
                          t.pagesLabel || 'Pages {{start}}–{{end}}',
                          selectedPage,
                          selectedPageEnd
                        )
                      : formatPage(
                          t.pageLabel || 'Page {{page}}',
                          selectedPage
                        )}
                  </DocumentMeta>
                </DocumentHeader>
                <PDFContainer>
                  <PDFViewer
                    url={
                      getPdfProxyUrl && selectedDoc.documentUrl
                        ? getPdfProxyUrl(selectedDoc.documentUrl)
                        : selectedDoc.documentUrl
                    }
                    initialPage={selectedPage - 1}
                    height="100%"
                    width="100%"
                    showThumbnails={false}
                    showToolbar={true}
                    enableSearch={true}
                    enableDownload={true}
                  />
                </PDFContainer>
              </PanelContainer>
            )}
          </PDFPanel>
        </SplitContainer>
      </ModalContainer>
    </Modal>
  )
}

// Styles — match DocumentViewerModal / PDFViewerPanel patterns exactly

const ReferenceList = styled.div`
  flex: 1;
  overflow-y: auto;
`

const ReferenceListItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.primary + '10' : 'transparent'};
  border: none;
  border-left: 3px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const RefIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.primary}10;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  margin-top: 2px;
`

const RefContent = styled.div`
  flex: 1;
  min-width: 0;
`

const RefTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.micro};
`

const RefMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Right panel styles — match PDFViewerPanel exactly

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.huge};
  text-align: center;
`

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme }) => theme.colors.background.tertiary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const EmptyStateTitle = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const EmptyStateDescription = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 400px;
`

const DocumentHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`

const DocumentTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.xsmall} 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const PDFContainer = styled.div`
  flex: 1;
  overflow: hidden;
`
